import ManageClientModal from "Components/Client/ManageClientModal";
import DashboardHeader from "Components/Dashboard/DashboardHeader";
import DashboardStatsOverview, {
  DashboardStat,
} from "Components/Dashboard/DashboardStatsOverview";
import Notifications from "Components/Dashboard/Notifications";
import RecentActivity from "Components/Dashboard/RecentActivity";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import {
  addNewClient,
  getConfig,
  getNotifications,
  getUserDetails,
} from "../../slices/thunks";
import HowCreateQuotesModal from "Components/Modals/HowCreateQuotesModal";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { AddClientFormParams } from "types/client.api";
import { createSelector } from "reselect";
import { selectDashboardState } from "slices/dashboard/reducer";
import { getDashboard } from "slices/dashboard/thunk";
import Loader from "Components/Common/Loader";
import { DashboardApiPayload } from "types/dashboard.api";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { selectNotificationsState } from "slices/notifications/reducer";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { clearItemState } from "slices/items/reducer";
import SelectPresetQuoteModal from "Components/Modals/SelectPresetQuoteModal";
import {
  clearInvoiceDetails,
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
} from "slices/invoices/reducer";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useScreenTour } from "tour/useScreenTour";
import { tourElementsIds } from "tour/tourElementsIds";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { IconName } from "Components/Icons/Icon";
import { ErrorToast } from "utils/toast";

const buildDashboardStats = (
  data: DashboardApiPayload,
  navigate: NavigateFunction,
): DashboardStat[] => {
  return [
    {
      title: "OUTSTANDING INVOICES",
      value: data.invoiceDetails.outstanding_invoices_amount,
      iconName: "BlueReceipt",
      iconBg: "#E5F0FF",
      prefix: "£",
      suffix: "",
      separator: ",",
      decimal: 2,
      onClick: () => {
        navigate("/invoices", { state: 1 });
      },
    },
    {
      title: "QUOTES AWAITING APPROVAL",
      value: data.quoteDetails.pending_quotes_amount,
      iconName: "OrangeWatch",
      iconBg: "#FEF1E7",
      prefix: "£",
      suffix: "",
      separator: ",",
      decimal: 2,
      onClick: () => {
        navigate("/quotes", { state: 1 });
      },
    },
    {
      title: "MONEY DUE THIS WEEK",
      value: data.financialSummary.money_due_this_week,
      iconName: "PoundGreen",
      iconBg: "#EBF5F1",
      prefix: "£",
      suffix: "",
      separator: ",",
      decimal: 2,
      onClick: () => {
        navigate("/invoices", { state: 2 });
      },
    },
    {
      title: "QUOTES ACCEPTED (Last 30 Days)",
      value: data.financialSummary.quotes_accepted_not_invoiced,
      iconName: "BlueReceipt",
      iconBg: "#E5F0FF",
      prefix: "",
      suffix: "",
      separator: ",",
      decimal: 0,
      onClick: () => {
        navigate("/quotes", { state: 2 });
      },
    },
  ];
};

const Dashboard = () => {
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showSelectPresetModal, setShowSelectPresetModal] = useState(false);
  const [showHowCreateQuoteModal, setShowHowCreateQuoteModal] = useState(false);
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const dispatch: any = useAppDispatch();
  const dashboardTour = useScreenTour("dashboard");
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const isImpersonated = useAppSelector((state) => state.auth.isImpersonated);
  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;
  const { presetQuoteList } = useAppSelector((state) => state?.presetQuote);

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner.",
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const toggleClientModal = () => {
    setShowAddClientModal((prev) => !prev);
  };

  const dashboardSelector = createSelector(selectDashboardState, (state) => ({
    dashboardData: state.dashboard,
    error: state.errors,
  }));

  const notificationSelector = createSelector(
    selectNotificationsState,
    (state) => ({
      notifications: state.notifications,
    }),
  );

  useEffect(() => {
    if (isImpersonated) {
      dispatch(getUserDetails());
      return;
    }
  }, [dispatch, isImpersonated, navigate]);

  useEffect(() => {
    dispatch(getConfig());
  }, [dispatch]);

  const { dashboardData, error } = useAppSelector(dashboardSelector);

  const { notifications } = useAppSelector(notificationSelector);

  const goToNotificationsPage = () => {
    navigate("/notifications-list");
  };

  useEffect(() => {
    dispatch(getDashboard());
    dispatch(getNotifications());
  }, [dispatch]);

  document.title = "Dashboard | EaziQuote";

  if (dashboardData === null) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader error={error} />
      </Container>
    );
  }

  return (
    <div className="page-content">
      <Container fluid className="gap-3 d-flex flex-column">
        <DashboardHeader
          onNewClientClick={toggleClientModal}
          onNewInVoiceClick={() => {
            if (hasNotSubscribed) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED);
            } else if (!hasBankAccount && !hasSignature) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(
                RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
              );
            } else if (!hasBankAccount) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED);
            } else if (!hasSignature) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.SIGNATURE_REQUIRED);
            } else {
              dispatch(clearQuoteDetails());
              dispatch(clearInvoiceDetails());
              dispatch(resetEditInvoiceStatus());
              dispatch(resetCreateInvoiceStatus());
              navigate("/manage-invoice");
            }
          }}
          onNewQuotesClick={() => {
            if (hasNotSubscribed) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED);
            } else if (!hasBankAccount && !hasSignature) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(
                RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
              );
            } else if (!hasBankAccount) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED);
            } else if (!hasSignature) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.SIGNATURE_REQUIRED);
            } else {
              setShowHowCreateQuoteModal(true);
            }
          }}
        />

        <Row className="g-3 g-lg-4 align-items-lg-stretch">
          {/* Stats */}
          <Col
            xxl={notifications.length > 0 ? "6" : "12"}
            xl={notifications.length > 0 ? "6" : "12"}
            lg="12"
          >
            <div
              className={`h-100 ${dashboardTour.open ? "pt-2 ps-1 pe-3" : ""}`}
              id={tourElementsIds.dashboardTour1}
            >
              <DashboardStatsOverview
                stats={buildDashboardStats(dashboardData, navigate)}
              />
            </div>
          </Col>

          {/* Notifications */}
          {notifications.length > 0 && (
            <Col xxl="6" xl="6" lg="12">
              <div className="h-100">
                <Notifications
                  notifications={
                    notifications.length > 5
                      ? notifications.slice(0, 5)
                      : notifications
                  }
                  onViewAllClick={goToNotificationsPage}
                />
              </div>
            </Col>
          )}
        </Row>

        <RecentActivity data={dashboardData} />
        <ManageClientModal
          isEdit={false}
          isOpen={showAddClientModal}
          initialData={null}
          toggleModal={toggleClientModal}
          onSubmit={(payload) => {
            dispatch(addNewClient(payload as AddClientFormParams));
          }}
        />
        <HowCreateQuotesModal
          show={showHowCreateQuoteModal}
          onStartFromScratchClick={() => {
            setShowHowCreateQuoteModal(false);
            dispatch(clearQuoteDetails());
            dispatch(clearItemState());
            navigate("/manage-quotes");
          }}
          onUsePresetClick={() => {
            setShowSelectPresetModal(true);
            setShowHowCreateQuoteModal(false);
          }}
          onCloseClick={() => {
            setShowHowCreateQuoteModal(false);
          }}
        />
        <SelectPresetQuoteModal
          isOpen={showSelectPresetModal}
          onClose={() => setShowSelectPresetModal(false)}
          onContinue={(template) => {
            console.log("Selected Template", template);
            if (!presetQuoteList.length) {
              ErrorToast(
                "No preset quote available. Please create a preset quote first.",
              );
              return;
            }
            if (template === null) {
              ErrorToast("Please select at least one preset quote.");
              return;
            }
            dispatch(clearQuoteDetails());
            dispatch(clearItemState());
            navigate("/manage-quotes", {
              state: { template },
            });
            setShowSelectPresetModal(false);
          }}
        />
        <OverlayGuide {...dashboardTour} />
        <RestrictedAccessModal
          isOpen={showSubscriptionExpiredPopUp}
          onCancel={() => setShowSubscriptionExpiredPopUp(false)}
          iconName={restrictionModalData.iconName}
          title={restrictionModalData.title}
          description={restrictionModalData.description}
          buttonText={restrictionModalData.buttonText}
          onButtonClick={restrictionModalData.onAction}
        />
      </Container>
    </div>
  );
};

export default Dashboard;
